import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { AppState } from '../../../shared/store/app-state';
import { CreateUserAccess, UpdateUserAccess, UserAccess } from '../../models/access';
import { BackOfficeUser } from '../../models/backOfficeUser';
import { AutomaticClosureStats, CMSAssociationResult } from '../../models/installer';
import { BackOfficeService } from '../../services/back.office.service';
import {
    loadInstallersCmsAssociation,
    loadUserAccess,
    removeUserAccess,
    setCurrentBackOfficeUserInfo,
} from '../actions/admin';

@Injectable()
export class AdminApi {
    constructor(
        private store: Store<AppState>,
        private backOfficeService: BackOfficeService,
    ) {}

    loadUserAccess(): Observable<UserAccess[]> {
        return this.backOfficeService.getAdminUsers().pipe(
            map((installers: UserAccess[]) => {
                this.store.dispatch(loadUserAccess({ users: installers }));
                return installers;
            }),
        );
    }

    updateUserAccess(user: CreateUserAccess | UpdateUserAccess): Observable<UserAccess> {
        return this.backOfficeService.updateUserAccess(user);
    }

    setUserPassword(password: string, verificationHash: string): Observable<void> {
        return this.backOfficeService.setUserPassword(password, verificationHash);
    }

    removeUserAccess(userId: string): Observable<string> {
        return this.backOfficeService.removeUserAccess(userId).pipe(
            map(() => {
                this.store.dispatch(removeUserAccess({ userAccess: userId }));
                return userId;
            }),
        );
    }

    resetPasswordForUserAccess(userEmail: string): Observable<void> {
        return this.backOfficeService.resetUserAccessPassword(userEmail);
    }

    getStats(): Observable<string> {
        return this.backOfficeService.getStats();
    }

    getCurrentBackOfficeUserInfo(): Observable<BackOfficeUser> {
        return this.backOfficeService
            .getCurrentBackOfficeUserInfo()
            .pipe(tap((user: BackOfficeUser) => this.store.dispatch(setCurrentBackOfficeUserInfo({ user }))));
    }

    advancedInstallerSearch(
        buList: string[],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fieldValues: any,
        strictMode = false,
    ): Observable<CMSAssociationResult[]> {
        // Get user installer info
        return this.backOfficeService.advancedInstallerSearch(buList, fieldValues, strictMode).pipe(
            tap((cmsAssociationsResult: CMSAssociationResult[]) =>
                this.store.dispatch(
                    loadInstallersCmsAssociation({
                        cmsAssociationResults: cmsAssociationsResult,
                    }),
                ),
            ),
        );
    }

    closeInstallerInterventions(installerId: string): Observable<AutomaticClosureStats> {
        return this.backOfficeService.closeAllPendingInterventions(installerId);
    }
}
